/**
 * useSelectedStore Composable
 * Simple adapter that delegates to selectedStore module getters
 * No business logic - all logic is in the store module (single source of truth)
 * Compatible with Vue 2
 */

/**
 * Composable for accessing selected store information
 * Delegates all business logic to selectedStore module getters
 * @param {Object} store - Vuex store instance
 * @returns {Object} Methods that delegate to store getters
 */
export function useSelectedStore(store) {
  if (!store) {
    console.warn("useSelectedStore: Store instance is required");
    return {
      getSelectedStore: () => null,
      getStoreTimezone: () => "UTC",
      getStoreStatus: () => "open",
      isStoreClosed: () => false,
      isScheduleEnabled: () => false,
      canScheduleOrder: () => false,
      getAvailableDiningOptions: () => [],
      getStoreWorkingHours: () => "Not Available",
      getNextOpenDay: () => null
    };
  }

  /**
   * Gets the currently selected store location
   * Delegates to store getter
   * @returns {Object|null} Selected store object or null
   */
  function getSelectedStore() {
    return store.getters["selectedStore/getSelectedLocation"] || null;
  }

  /**
   * Gets the timezone for the selected store
   * Delegates to store getter
   * @returns {string} Store timezone or "UTC" as fallback
   */
  function getStoreTimezone() {
    return store.getters["selectedStore/getStoreTimezone"] || "UTC";
  }

  /**
   * Gets the store status
   * Delegates to store getter (single source of truth)
   * @returns {string} 'open' | 'closed' | 'schedule_only'
   */
  function getStoreStatus() {
    return store.getters["selectedStore/getStoreStatus"] || "open";
  }

  /**
   * Checks if the store is closed
   * Delegates to store getter
   * @returns {boolean} True if store is closed, false otherwise
   */
  function isStoreClosed() {
    return store.getters["selectedStore/isStoreClosed"] || false;
  }

  /**
   * Checks if the store supports scheduled orders
   * Delegates to store getter
   * @returns {boolean} True if store supports scheduled orders
   */
  function isScheduleEnabled() {
    return store.getters["selectedStore/isScheduleEnabled"] || false;
  }

  /**
   * Determines if an order can be scheduled
   * Delegates to store getter
   * @returns {boolean} True if order can be scheduled
   */
  function canScheduleOrder() {
    return store.getters["selectedStore/canScheduleOrder"] || false;
  }

  /**
   * Gets filtered available dining options
   * Delegates to store getter
   * @returns {Array} Filtered array of available dining options
   */
  function getAvailableDiningOptions() {
    return store.getters["selectedStore/getAvailableDiningOptions"] || [];
  }

  /**
   * Gets formatted working hours string
   * Delegates to store getter
   * @returns {string} Formatted hours string or "Not Available"
   */
  function getStoreWorkingHours() {
    return (
      store.getters["selectedStore/getStoreWorkingHours"] || "Not Available"
    );
  }

  /**
   * Gets the next open day information
   * Delegates to store action
   * @returns {Promise<Object|null>} Object with { dayName, dayDate, hours } or null
   */
  async function getNextOpenDay() {
    return await store.dispatch("selectedStore/getNextOpenDay");
  }

  return {
    getSelectedStore,
    getStoreTimezone,
    getStoreStatus,
    isStoreClosed,
    isScheduleEnabled,
    canScheduleOrder,
    getAvailableDiningOptions,
    getStoreWorkingHours,
    getNextOpenDay
  };
}
